// @flow
import React, { Children, useContext, useEffect, useRef, useState, type Node } from 'react';
import classnames from 'classnames';
import { throttle } from 'lodash';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { hasTouch } from 'Components/utils/feature-testing';

import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton.jsx';

import { themes } from './Slider.themes';

if (
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  // $FlowFixMe
  !('scrollBehavior' in document.documentElement.style)
) {
  // $FlowFixMe
  require('smoothscroll-polyfill').polyfill();
}

type Props = {
  children: Array<Node>,
  className?: string,
  cols?: number,
  hasControls?: boolean,
  hasTabindex?: boolean,
  id?: string,
  initialIndex?: number,
  navPosition?: 'bottom' | 'center' | 'centerOverlap' | 'top' | 'side',
  nbItemsToScroll?: number,
  nextLabel?: string,
  onChangeIndex?: (activeIndex: number) => void,
  onNext?: (e?: Event) => void,
  onPrevious?: (e?: Event) => void,
  previousLabel?: string,
  scrollAreaClassName?: string,
  scrollAreaContainerClassName?: string,
  scrollControlsClassName?: string,
  scrollItemClassName?: string,
  theme?: $Keys<typeof themes>,
};

export function Slider(props: Props) {
  const {
    className,
    children,
    cols = 1,
    hasControls,
    hasTabindex,
    id,
    initialIndex = 0,
    navPosition = 'top',
    nbItemsToScroll = 1,
    nextLabel,
    onChangeIndex,
    onNext,
    onPrevious,
    previousLabel,
    scrollAreaClassName,
    scrollAreaContainerClassName,
    scrollControlsClassName,
    scrollItemClassName,
    theme = 'default',
  } = props;

  const childrenCount = Children.count(children);

  const containerRef: { current: ?HTMLElement } = useRef(null);
  const refsArray: { current: Array<?HTMLElement> } = useRef([]);

  const { isRTL } = useContext(ResponsiveContext);

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [reachedX, setReachedX] = useState({
    hasReachedLeft: false,
    hasReachedRight: false,
  });
  const [hiddenIndexes, setHiddenIndexes] = useState(Children.map(children, () => false));

  const [prevInitialIndex, setPrevInitialIndex] = useState(null);

  useEffect(
    () => {
      if (onChangeIndex) {
        onChangeIndex(activeIndex);
      }
    },
    [activeIndex],
  );

  const getHiddenIndexes = () => {
    if (cols > 0) {
      return;
    }

    if (containerRef.current) {
      const { clientWidth, scrollLeft, offsetLeft: containerOffsetLeft } = containerRef.current;

      const indexes = refsArray.current.filter(Boolean).map(({ offsetLeft, offsetWidth }) => {
        return (
          offsetLeft < scrollLeft + containerOffsetLeft ||
          offsetLeft + offsetWidth > scrollLeft + containerOffsetLeft + clientWidth
        );
      });

      setHiddenIndexes(indexes);
    }
  };

  const getReachedX = () => {
    if (containerRef.current) {
      const { clientWidth, scrollLeft, scrollWidth } = containerRef.current;

      const hasReachedLeft = scrollLeft === 0;
      const hasReachedRight = scrollLeft + clientWidth >= scrollWidth;

      setReachedX({
        hasReachedLeft,
        hasReachedRight,
      });
    }
  };

  // get index in possible range
  const getSafeIndex = idx => {
    if (idx < 0) {
      return 0;
    }
    const maxIndex = cols > 0 ? childrenCount - cols : childrenCount - 1;
    return idx > maxIndex ? maxIndex : idx;
  };

  const scrollToIndex = (index, e) => {
    if (e) {
      e.preventDefault();
    }

    const idx = getSafeIndex(index);

    const scrollValue = refsArray.current.slice(0, idx).reduce((v, ref) => {
      // $FlowFixMe
      return v + ref.offsetWidth;
    }, 0);

    if (containerRef.current?.scrollTo) {
      containerRef.current.scrollTo({
        behavior: 'smooth',
        left: scrollValue,
      });
    }
  };

  useEffect(
    () => {
      getReachedX();
      getHiddenIndexes();
      scrollToIndex(initialIndex);
      setPrevInitialIndex(initialIndex);
    },
    [children],
  );

  useEffect(
    () => {
      if (initialIndex !== prevInitialIndex) {
        scrollToIndex(initialIndex);
        setPrevInitialIndex(initialIndex);
      }
    },
    [initialIndex],
  );

  if (!childrenCount) {
    return null;
  }

  const onScroll = throttle(() => {
    getReachedX();
    getHiddenIndexes();

    // $FlowFixMe
    const { offsetLeft, scrollLeft } = containerRef.current;

    const positions = refsArray.current.filter(Boolean).map(ref => ref.offsetLeft);

    const closest = positions.reduce((prev, curr) => {
      return Math.abs(curr - scrollLeft) < Math.abs(prev - scrollLeft) ? curr : prev;
    }, offsetLeft);

    let pos = positions.indexOf(closest);

    if (pos === activeIndex && pos < childrenCount - 1) {
      pos++;
    }

    if (activeIndex > pos) {
      onPrevious && onPrevious();
    } else if (activeIndex < pos) {
      onNext && onNext();
    }

    if (pos > -1) {
      setActiveIndex(pos);
      const el = refsArray.current[pos];

      // $FlowFixMe
      const elFocused = hasTabindex ? el : el.firstChild;
      // $FlowFixMe
      elFocused.focus({ preventScroll: true });
    }
  }, 100);

  const {
    thControl,
    thControlLeft,
    thControlRight,
    thControlColor,
    thControlColorBg,
    thControlDisabled,
    thControls,
    thInner,
    thRoot,
  } = themes[theme]({
    navPosition,
  });

  const { hasReachedLeft, hasReachedRight } = reachedX;

  const isSnapScrollDisabled = isRTL && hasTouch();

  return (
    <div
      className={classnames(className, thRoot, 'relative', { '-scale-x-1': isRTL })}
      dir="ltr"
      id={id}
    >
      <div className={classnames('h-full', thInner)}>
        <div className={scrollAreaClassName}>
          <div
            className={classnames(
              'flex flex-row flex-no-wrap w-full',
              scrollAreaContainerClassName,
            )}
            onScroll={onScroll}
            ref={containerRef}
          >
            {Children.map(children, (child, idx) => {
              const isHidden =
                cols > 0 ? idx < activeIndex || idx >= activeIndex + cols : hiddenIndexes[idx];

              return (
                <div
                  aria-hidden={isHidden}
                  className={classnames(scrollItemClassName, 'image-responsive-cell', {
                    'scroll-snap-align-start': !isSnapScrollDisabled,
                    [`w-1/${cols}`]: cols > 1,
                    'w-full': cols === 1,
                    '-scale-x-1': isRTL,
                  })}
                  key={idx}
                  ref={ref => {
                    refsArray.current[idx] = ref;
                  }}
                  {...(hasTabindex ? { tabIndex: -1 } : null)}
                >
                  {React.cloneElement(child, { dir: isRTL ? 'rtl' : 'ltr', isHidden })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {hasControls &&
        !(hasReachedLeft && hasReachedRight) && (
          <div className={classnames(scrollControlsClassName, thControls)}>
            <ArrowButton
              className={classnames(thControl, thControlLeft, {
                [thControlDisabled]: hasReachedLeft,
              })}
              color={thControlColor}
              colorBg={thControlColorBg}
              hasNoRtl
              isDisabled={hasReachedLeft}
              label={previousLabel}
              onClick={e => scrollToIndex(activeIndex - nbItemsToScroll, e)}
            />
            <ArrowButton
              className={classnames(thControl, thControlRight, {
                [thControlDisabled]: hasReachedRight,
              })}
              color={thControlColor}
              colorBg={thControlColorBg}
              hasNoRtl
              isDisabled={hasReachedRight}
              label={nextLabel}
              onClick={e => scrollToIndex(activeIndex + nbItemsToScroll, e)}
              rotation={180}
            />
          </div>
        )}
    </div>
  );
}
